// src/content/figures/BuilderAppDecision.jsx
// Inline SVG infographic: does a solo builder's app need an EU representative?
// Real text (indexable, selectable, screen-reader accessible).
// Explicit colours so it renders predictably regardless of theme tokens.

const C = {
  ink: '#1e293b',
  muted: '#5b6472',
  line: '#94a3b8',
  boxFill: '#f3f6fb',
  boxStroke: '#cbd5e1',
  white: '#ffffff',
  accent: '#186be6',
};

const q = { fill: C.boxFill, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const end = { fill: C.white, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const win = { fill: C.accent, stroke: 'none', rx: 10 };
const t = { fontFamily: 'inherit', fontSize: 15, fill: C.ink };
const s = { fontFamily: 'inherit', fontSize: 13, fill: C.muted };
const lab = { fontFamily: 'inherit', fontSize: 13, fill: C.ink, fontWeight: 600 };
const onAccent = { fontFamily: 'inherit', fontSize: 15, fill: C.white };
const edge = { stroke: C.line, strokeWidth: 1.5, fill: 'none', markerEnd: 'url(#bld-arrow)' };

export default function BuilderAppDecision() {
  return (
    <svg
      viewBox="0 0 930 710"
      width="100%"
      role="img"
      aria-labelledby="bld-title bld-desc"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', maxWidth: 930 }}
    >
      <title id="bld-title">Does your app need an EU representative?</title>
      <desc id="bld-desc">
        Decision flow. One: can people in the EU sign up for your app or pay for it? Free apps count too; a site people can merely reach does not. If yes, go to question three. If no, question two: do you use analytics, ads or tracking to follow what individual users do? If no, you need no EU representative; re-check when you open signups to the EU. If yes, go to question three: do you have an office or company in the EU? An EU server region does not count. If yes, this rule does not apply, because it covers only apps run from outside the EU. If no, question four: are all three true, namely the handling is only occasional and not part of your normal activity, there is no sensitive data or criminal records at scale, and it is unlikely to put people at risk? Few users is not occasional; a live app with daily signups is regular. If all three are true you are exempt, which is rare for a live app; write down why and re-check. Otherwise, you need an EU representative. Side note: fast fix, appoint one in minutes.
      </desc>
      <defs>
        <marker id="bld-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.line} />
        </marker>
      </defs>

      {/* Column guide: left x=20-250 (c=135) · centre x=300-660 (c=480; Q4 wide 280-680) · right x=710-910 (c=810) */}

      {/* Q1 */}
      <rect x="300" y="20" width="360" height="90" {...q} />
      <text x="480" y="52" textAnchor="middle" {...t}>Can people in the EU sign up or pay?</text>
      <text x="480" y="76" textAnchor="middle" {...s}>Free apps count too.</text>
      <text x="480" y="95" textAnchor="middle" {...s}>A site people can merely reach does not.</text>

      {/* Q1 → Yes (down to Q3) */}
      <path d="M 480 110 L 480 270" {...edge} />
      <text x="492" y="195" {...lab}>Yes</text>

      {/* Q1 → No (right, then down to Q2) */}
      <path d="M 660 65 L 810 65 L 810 150" {...edge} />
      <text x="690" y="56" {...lab}>No</text>

      {/* Q2 */}
      <rect x="710" y="150" width="200" height="94" {...q} />
      <text x="810" y="176" textAnchor="middle" {...t}>Do you use analytics,</text>
      <text x="810" y="196" textAnchor="middle" {...t}>ads or tracking?</text>
      <text x="810" y="219" textAnchor="middle" {...s}>to follow what</text>
      <text x="810" y="236" textAnchor="middle" {...s}>individual users do</text>

      {/* Q2 → Yes (left, then down into Q3) */}
      <path d="M 710 197 L 590 197 L 590 270" {...edge} />
      <text x="668" y="188" {...lab}>Yes</text>

      {/* Q2 → No (down) */}
      <path d="M 810 244 L 810 294" {...edge} />
      <text x="822" y="275" {...lab}>No</text>

      {/* Q2a */}
      <rect x="710" y="294" width="200" height="96" {...end} />
      <text x="810" y="320" textAnchor="middle" {...t}>No EU representative</text>
      <text x="810" y="340" textAnchor="middle" {...t}>needed.</text>
      <text x="810" y="363" textAnchor="middle" {...s}>Re-check when you</text>
      <text x="810" y="380" textAnchor="middle" {...s}>open signups to the EU.</text>

      {/* Q3 */}
      <rect x="300" y="270" width="360" height="86" {...q} />
      <text x="480" y="298" textAnchor="middle" {...t}>Do you have an office or</text>
      <text x="480" y="319" textAnchor="middle" {...t}>company in the EU?</text>
      <text x="480" y="341" textAnchor="middle" {...s}>An EU server region does not count.</text>

      {/* Q3 → Yes (left) */}
      <path d="M 300 313 L 250 313" {...edge} />
      <text x="262" y="304" {...lab}>Yes</text>
      <rect x="20" y="268" width="230" height="90" {...end} />
      <text x="135" y="298" textAnchor="middle" {...t}>This rule does not apply.</text>
      <text x="135" y="321" textAnchor="middle" {...s}>It covers only apps run</text>
      <text x="135" y="339" textAnchor="middle" {...s}>from outside the EU.</text>

      {/* Q3 → No (down) */}
      <path d="M 480 356 L 480 400" {...edge} />
      <text x="492" y="383" {...lab}>No</text>

      {/* Q4 */}
      <rect x="280" y="400" width="400" height="152" {...q} />
      <text x="480" y="426" textAnchor="middle" {...t}>Are all three true?</text>
      <text x="480" y="450" textAnchor="middle" {...s}>1. Only occasional, not part of your normal activity</text>
      <text x="480" y="470" textAnchor="middle" {...s}>2. No sensitive data or criminal records at scale</text>
      <text x="480" y="490" textAnchor="middle" {...s}>3. Unlikely to put people at risk</text>
      <rect x="296" y="502" width="368" height="38" fill="#fff4e0" stroke="none" rx="6" />
      <text x="480" y="518" textAnchor="middle" fontFamily="inherit" fontSize="12.5" fill="#7a4a00" fontWeight="600">Few users is not "occasional".</text>
      <text x="480" y="533" textAnchor="middle" fontFamily="inherit" fontSize="12.5" fill="#7a4a00">A live app with daily signups is regular.</text>

      {/* Q4 → Yes, all three (left) */}
      <path d="M 280 476 L 135 476 L 135 610" {...edge} />
      <text x="147" y="570" {...lab}>Yes, all three</text>
      <rect x="20" y="610" width="230" height="80" {...end} />
      <text x="135" y="638" textAnchor="middle" {...t}>Exempt.</text>
      <text x="135" y="660" textAnchor="middle" {...s}>Rare for a live app.</text>
      <text x="135" y="678" textAnchor="middle" {...s}>Write down why; re-check.</text>

      {/* Q4 → No (down) */}
      <path d="M 480 552 L 480 610" {...edge} />
      <text x="492" y="587" {...lab}>No</text>

      {/* Result */}
      <rect x="330" y="610" width="300" height="80" {...win} />
      <text x="480" y="643" textAnchor="middle" {...onAccent} fontWeight="600">You need an</text>
      <text x="480" y="666" textAnchor="middle" {...onAccent} fontWeight="600">EU representative</text>

      {/* Result - - Fast fix (dashed, no arrowhead) */}
      <path d="M 630 650 L 710 650" stroke={C.line} strokeWidth="1.5" strokeDasharray="4 4" fill="none" />
      <rect x="710" y="615" width="200" height="70" {...end} />
      <text x="810" y="643" textAnchor="middle" {...t}>Fast fix:</text>
      <text x="810" y="664" textAnchor="middle" {...s}>appoint one in minutes</text>
    </svg>
  );
}
